/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaBuilderAny,
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	ISchemaDataSourceModel,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	ISelectionItem_Base,
	isSchemaBuilderAnyModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaFlowNodeListModel,
	isSchemaFlowNodeModel,
	TGenericBlueprintSchema,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { EditSelection, TEditSelectionItem } from "@adapptio/lib-components";
import {
	createEventEmitter,
	emitEvent,
	isArray,
	onEvent,
	TSimpleEventEmitter
} from "@adapptio/lib-shared";

export enum VIEW_SELECTION_ITEM_TYPE {
	DATASOURCE = "dataSource",
	VIEW_PARAMETER = "viewParameter",
	VIEW_PARAMETER_ANY = "viewParameterAny",
	VIEW_OUTLET = "viewOutlet",
	// Copied from the EditSelection:
	COMPONENT = "component",
	COMPONENT_LIST = "componentList"
}

export enum VIEW_SELECTION_EVENT_TYPE {
	COMPONENT_EVENT = "componentEvent",
	DATASOURCE_EVENT = "dataSourceEvent"
}

export interface ISelectionItem_DataSource extends ISelectionItem_Base {
	/** Item type */
	type: VIEW_SELECTION_ITEM_TYPE.DATASOURCE;
}

export interface ISelectionItem_Outlet extends ISelectionItem_Base {
	type: VIEW_SELECTION_ITEM_TYPE.VIEW_OUTLET;
	parentMapModelId: number;
	mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>;
}

export interface ISelectionItem_Parameter extends ISelectionItem_Base {
	type: VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER;
	parentMapModelId: number;
	mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>;
}

export interface ISelectionItem_ParameterAny extends ISelectionItem_Base {
	type: VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER_ANY;
	parentMapModelId: number;
	mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>;
}

export type TSelectableSchemaBuilder = ISchemaBuilderAny;

export type TViewEditSelectionItem =
	| TEditSelectionItem
	| ISelectionItem_DataSource
	| ISelectionItem_Parameter
	| ISelectionItem_ParameterAny
	| ISelectionItem_Outlet;

export interface ISelectionEvent_Node {
	type: VIEW_SELECTION_EVENT_TYPE;
	/** Id of selected event node */
	nodeId: number;
	nodeListModelId: number;
}

export interface ISelectionComponentEvent_Node extends ISelectionEvent_Node {
	type: VIEW_SELECTION_EVENT_TYPE.COMPONENT_EVENT;
}

export interface ISelectionDataSourceEvent_Node extends ISelectionEvent_Node {
	type: VIEW_SELECTION_EVENT_TYPE.DATASOURCE_EVENT;
}

export type TViewEditSelectionEvent = ISelectionComponentEvent_Node|ISelectionDataSourceEvent_Node;

export type TViewSelectionItemOrEvent = TViewEditSelectionItem | TViewEditSelectionEvent;

/**
 * Type guard for parameter selection
 * @param value
 */
export function isSelectionItemDataSource(
	value: TViewSelectionItemOrEvent
): value is ISelectionItem_DataSource {
	return value?.type === VIEW_SELECTION_ITEM_TYPE.DATASOURCE;
}

/**
 * Type guard for parameter selection
 * @param value
 */
export function isSelectionItemOutlet(value: TViewSelectionItemOrEvent): value is ISelectionItem_Outlet {
	return value?.type === VIEW_SELECTION_ITEM_TYPE.VIEW_OUTLET;
}

/**
 * Type guard for parameter selection
 * @param value
 */
export function isSelectionItemParameter(
	value: TViewSelectionItemOrEvent
): value is ISelectionItem_Parameter {
	return value?.type === VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER;
}

/**
 * Type guard for parameter any selection
 * @param value
 */
export function isSelectionItemParameterAny(
	value: TViewSelectionItemOrEvent
): value is ISelectionItem_ParameterAny {
	return value?.type === VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER_ANY;
}

/**
 * Type guard for component event selection
 * @param value
 */
export function isSelectionComponentEvent(
	value: TViewSelectionItemOrEvent
): value is ISelectionComponentEvent_Node {
	return value?.type === VIEW_SELECTION_EVENT_TYPE.COMPONENT_EVENT;
}

/**
 * Type guard for component event selection
 * @param value
 */
export function isSelectionDataSourceEvent(
	value: TViewSelectionItemOrEvent
): value is ISelectionDataSourceEvent_Node {
	return value?.type === VIEW_SELECTION_EVENT_TYPE.DATASOURCE_EVENT;
}

/**
 * Class to manage view model selections
 */
export class ViewEditSelection extends EditSelection<TViewEditSelectionItem> {
	/** Selected event */
	private eventName: string;

	/** Event parent node ID - to detect changes */
	private eventRefNodeId: number;

	/** Selected event nodes */
	private eventNodes: ISelectionEvent_Node[] = [];

	/** Event selection change event */
	public onEventChange: TSimpleEventEmitter<void> = createEventEmitter();

	/** Event nodes selection change event */
	public onEventNodesChange: TSimpleEventEmitter<void> = createEventEmitter();

	public constructor() {
		super();

		onEvent(this.onChange, this.handleSelectionChange.bind(this));
	}

	/**
	 * Returns if data source is selected
	 *
	 * @param dsModel DataSource model
	 */
	public isDataSourceSelected(dsModel: ISchemaDataSourceModel): boolean {
		if (!dsModel) {
			return false;
		}

		const index = this.findItemIndexByNodeId(dsModel.nodeId);

		return isSelectionItemDataSource(this.items[index]);
	}

	/**
	 * Selects data source model
	 * Return is data source was (de)selected or not
	 *
	 * @param dsModel DataSource model
	 */
	public selectDataSource(dsModel: ISchemaDataSourceModel): boolean {
		if (!dsModel) {
			return false;
		}

		const isSelected = this.isDataSourceSelected(dsModel);

		// In multiselect, de-selected the component, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectDataSource(dsModel);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem = {
			type: VIEW_SELECTION_ITEM_TYPE.DATASOURCE,
			nodeId: dsModel.nodeId
		} as TViewEditSelectionItem;

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Deselected all
	 */
	public deselectAll() {
		this.unselectAll();
		emitEvent(this.onChange);
	}

	/**
	 * Deselected the data source (if was selected) and returns if selection changes
	 *
	 * @param dsModel DataSource model
	 */
	public deselectDataSource(dsModel: ISchemaDataSourceModel): boolean {
		if (!dsModel) {
			return false;
		}

		return this.deselectByNodeId(dsModel.nodeId);
	}

	/**
	 * Tries to find event node inde by node ID
	 *
	 * @param nodeId Node ID
	 * @returns Index of found node or -1 when not found
	 */
	protected findEventNodeIndexByNodeId(nodeId: number): number {
		for (let i = 0; i < this.eventNodes.length; i++) {
			if (this.eventNodes[i].nodeId === nodeId) {
				return i;
			}
		}

		return -1;
	}

	/**
	 * Returns if any event is selected
	 */
	public isEventSelected(): boolean {
		return !!this.eventName;
	}

	/**
	 * Returns if some event nodes are selected (and event is selected)
	 */
	public areSomeEventNodesSelected(): boolean {
		return !!this.eventName && this.eventNodes.length > 0;
	}

	/**
	 * Selects an event
	 *
	 * @param eventRefModel Event ref model (to detect changes and clear nodes selection)
	 * @param eventName Event name
	 */
	public selectEvent(eventRefModel: TGenericModelNode, eventName: string): void {
		// Model is not set
		if (!eventRefModel) {
			return;
		}

		// Already selected
		if (this.eventName === eventName && this.eventRefNodeId === eventRefModel.nodeId) {
			return;
		}

		this.eventName = eventName;
		this.eventRefNodeId = eventRefModel.nodeId;

		this.deselectAllEventNodes();
		emitEvent(this.onEventChange);
	}

	/**
	 * Returns if an event node is selected
	 *
	 * @param eventNodeModel Event node model
	 */
	public isEventNodeSelected(
		eventNodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>
	): boolean {
		if (!eventNodeModel) {
			return false;
		}

		return this.eventNodes.some((item) => item.nodeId === eventNodeModel?.nodeId);
	}

	/**
	 * Returns if an event node is selected by node ID
	 *
	 * @param eventNodeId Event node ID
	 */
	public isEventNodeIdSelected(eventNodeId: number): boolean {
		return this.eventNodes.some((item) => item.nodeId === eventNodeId);
	}

	/**
	 * Selects an event node model
	 *
	 * @param eventNodeModel Event node model
	 */
	public selectEventNode(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>
	): void {
		if (!isSchemaFlowNodeListModel(nodeList) || !isSchemaFlowNodeModel(nodeModel)) {
			return;
		}

		if (!this.eventName && !this.eventRefNodeId) {
			console.warn("No event selected");
			return;
		}

		const isSelected = this.isEventNodeSelected(nodeModel);

		// In multiselect, de-selected the node, otherwise it's already selected so do nothing
		if (isSelected) {
			if (this.multiSelect) {
				this.deselectEventNode(nodeModel);
			}

			return;
		}

		const selItem: ISelectionEvent_Node = {
			type: VIEW_SELECTION_EVENT_TYPE.COMPONENT_EVENT,
			nodeId: nodeModel.nodeId,
			nodeListModelId: nodeList.nodeId
		};

		if (this.multiSelect) {
			this.eventNodes.push(selItem);
		} else {
			this.eventNodes = [ selItem ];
		}

		emitEvent(this.onEventNodesChange);
	}

	/**
	 * Deselects an event node (if was selected)
	 *
	 * @param eventNodeModel Event node model
	 */
	public deselectEventNode(eventNodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>): void {
		if (!isSchemaFlowNodeModel(eventNodeModel)) {
			return;
		}

		this.deselectEventNodeById(eventNodeModel.nodeId);
	}

	public deselectEventNodeById(nodeId: number): void {
		const i = this.findEventNodeIndexByNodeId(nodeId);

		if (i >= 0) {
			this.eventNodes.splice(i, 1);
			emitEvent(this.onEventNodesChange);
		}
	}

	/**
	 * Sets selected event nodes (replaces previous one)
	 *
	 * @param eventNodeModels Models of selected event nodes
	 */
	public setEventNodes(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModels: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>[]
	): void {
		if (!isArray(nodeModels)) {
			return;
		}

		if (!this.eventName && !this.eventRefNodeId) {
			console.warn("No event selected");
			return;
		}

		this.eventNodes = nodeModels.map((model) => ({
			type: VIEW_SELECTION_EVENT_TYPE.COMPONENT_EVENT,
			nodeId: model.nodeId,
			nodeListModelId: nodeList.nodeId
		}));

		emitEvent(this.onEventNodesChange);
	}

	/**
	 * Returns selected event name (or null if no event is selected)
	 */
	public getEventName(): string {
		return this.eventName;
	}

	/**
	 * Returns selected event nodes
	 */
	public getEventNodes(): ISelectionEvent_Node[] {
		return this.eventNodes;
	}

	/**
	 * Deselects all event nodes
	 */
	public deselectAllEventNodes(): void {
		this.eventNodes = [];
		emitEvent(this.onEventNodesChange);
	}

	/**
	 * Deselects an event (if was selected)
	 *
	 * @param eventName Event name
	 */
	public deselectEvent(eventName: string): void {
		if ((eventName && this.eventName === eventName) || !eventName) {
			this.deselectAllEventNodes();

			this.eventName = null;
			emitEvent(this.onEventChange);
		}
	}

	/**
	 * Selects parameter model
	 * Returns true if parameter was (de)selected or not
	 */
	public selectParameter(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel)) {
			return false;
		}

		const isSelected = this.isParameterSelected(parentModel, mapEntry);

		// In multiselect, de-selected the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectParameter(parentModel, mapEntry);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Parameter | ISelectionItem_ParameterAny = {
			type: isSchemaBuilderAnyModel(mapEntry.value)
				? VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER_ANY
				: VIEW_SELECTION_ITEM_TYPE.VIEW_PARAMETER,
			nodeId: mapEntry.key.nodeId,
			parentMapModelId: parentModel.nodeId,
			mapEntry: mapEntry
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if parameter is selected
	 */
	public isParameterSelected(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(mapEntry.key.nodeId);
		const item = this.items[index];

		if (isSelectionItemParameter(item) || isSelectionItemParameterAny(item)) {
			return item.parentMapModelId === parentModel.nodeId;
		}

		return false;
	}

	/**
	 * Deselect the parameter (if was selected) and return true if selection changes
	 */
	public deselectParameter(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		if (!this.isParameterSelected(parentModel, mapEntry)) {
			return false;
		}

		return this.deselectByNodeId(mapEntry.key.nodeId);
	}

	/**
	 * Selects outlet model
	 * Returns true if outlet was (de)selected or not
	 */
	public selectOutlet(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel)) {
			return false;
		}

		const isSelected = this.isOutletSelected(parentModel, mapEntry);

		// In multiselect, de-selected the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectOutlet(parentModel, mapEntry);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Outlet = {
			type: VIEW_SELECTION_ITEM_TYPE.VIEW_OUTLET,
			nodeId: mapEntry.key.nodeId,
			parentMapModelId: parentModel.nodeId,
			mapEntry: mapEntry
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if outlet is selected
	 */
	public isOutletSelected(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(parentModel.nodeId);
		const item = this.items[index];

		if (isSelectionItemOutlet(item)) {
			return item.parentMapModelId === parentModel.nodeId;
		}

		return false;
	}

	/**
	 * Deselect the outlet and return true if selection changes
	 */
	public deselectOutlet(
		parentModel: ISchemaConstMapModel<TGenericBlueprintSchema>,
		mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		if (!this.isOutletSelected(parentModel, mapEntry)) {
			return false;
		}

		return this.deselectByNodeId(mapEntry.key.nodeId);
	}

	/**
	 * Handles selction change
	 * Clears selected event nodes if some other subject is selected
	 */
	private handleSelectionChange() {
		if (this.eventRefNodeId || this.eventNodes.length > 0) {
			if (this.items.length > 1 || !this.isNodeSelected(this.eventRefNodeId)) {
				this.eventRefNodeId = null;
				this.deselectAllEventNodes();
			}
		}
	}
}
